/*
 * Creating a release note (S42, 2026-10-12).
 *
 * NewArticlePage, copied and simplified, for the reasons that page gives: one
 * form, one request, one transaction - the kb.ReleaseNote row and its first
 * revision written together, because a note with no revision has no title and
 * cannot go live whatever its gates say. Created hidden by default, as
 * kb.ReleaseNote.IsVisible defaults to 0.
 *
 * What is different from an article:
 *
 *   - The address is the PRODUCT and the VERSION, not a slug: the owner's answer
 *     is one note per product and version, enforced by
 *     UQ_ReleaseNote_ProductId_Version, with no slug of its own. So there is no
 *     slug to suggest or suffix. A pair already taken comes back from the server
 *     as a 409 whose message belongs under the Version field, and that is where
 *     it is shown - suffixing a version would invent a release that never
 *     happened.
 *   - The version box pins its v (ReleaseNoteVersionInput); what is sent always
 *     starts with exactly one.
 *   - The title is ONE line of at most 200 characters - the column is
 *     NVARCHAR(200), from the shared constant, not the article's 300.
 *   - No categories, so the missing-metadata guard asks for products only: an
 *     empty category catalogue must not stop a release note being written.
 *   - No videos: the editor hides "Insert video" and its preview leaves the
 *     token as text.
 *
 * The editor has no image owner yet - there is no note to attach to until this
 * form has created it - so an image is explained ("save the release note
 * first") rather than uploaded, exactly as on the new-article page.
 *
 * Its words come from the chosen language's dictionary, `t.admin.releaseNotes` (S48).
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ADMIN_RELEASE_NOTES_PATH,
  RELEASE_NOTE_TITLE_MAX_LENGTH,
  RELEASE_NOTE_VERSION_PREFIX,
  adminReleaseNoteHref,
  fromLocalDateTimeInputValue,
  isValidReleaseNoteVersion,
  toLocalDateTimeInputValue,
} from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Field, Loading } from '../components/ui.js';
import { ApiError } from '../api/client.js';
import { ProductSelect } from './components/ProductSelect.js';
import { MarkdownEditor } from './components/MarkdownEditor.js';
import { ReleaseNoteVersionInput } from './components/ReleaseNoteVersionInput.js';
import { releaseNoteTitleError } from './ReleaseNoteEditorPage.js';
import { useMessages } from '../i18n/language.js';

/** /admin/releasenotes/new - the row and its first revision, created in one request. */
export function NewReleaseNotePage(): JSX.Element {
  const t = useMessages();
  const words = t.admin.releaseNotes;
  const api = useAdminApi();
  const navigate = useNavigate();
  const action = useAction();

  const metadata = useAsync(() => api.metadata(), []);

  // Null rather than "the first product", for NewArticlePage's reason: a
  // pre-selected answer would make the required field look answered.
  const [productId, setProductId] = useState<number | null>(null);
  const [version, setVersion] = useState<string>(RELEASE_NOTE_VERSION_PREFIX);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [publishMode, setPublishMode] = useState<'hidden' | 'now' | 'schedule'>('hidden');
  const [publishAt, setPublishAt] = useState(() => toLocalDateTimeInputValue(new Date()));

  /*
   * The server's "that product already has this version" - a 409 carrying its
   * sentence under `version`. Shown under the field rather than in the banner,
   * and cleared the moment either half of the pair moves, because the verdict was
   * about the pair that was sent.
   */
  const duplicateFromServer =
    action.error instanceof ApiError && action.error.conflict === 'duplicate'
      ? action.error
      : null;

  const titleError = releaseNoteTitleError(title, words);
  // The same words the server answers with, from releaseNotePropertiesSchema.
  const productError = productId === null ? words.create.productRequired : undefined;
  const versionError = !isValidReleaseNoteVersion(version)
    ? t.admin.rules.releaseNoteVersionRequirement
    : duplicateFromServer
      ? (duplicateFromServer.fieldError('version') ?? duplicateFromServer.message)
      : undefined;

  const blocked = Boolean(titleError || productError || versionError);

  // The scheduled instant as it will be stored, for the hint under the picker.
  const storedAt = fromLocalDateTimeInputValue(publishAt);

  const submit = async () => {
    if (blocked || productId === null) return;

    const publishAtUtc =
      publishMode === 'schedule'
        ? (fromLocalDateTimeInputValue(publishAt) ?? new Date().toISOString())
        : new Date().toISOString();

    const created = await action.run(() =>
      api.createReleaseNote({
        productId,
        version,
        publishAtUtc,
        isVisible: publishMode !== 'hidden',
        title: title.trim(),
        content,
        changeNote: 'Created.',
      }),
    );

    if (created) navigate(adminReleaseNoteHref(created.releaseNoteId));
  };

  if (metadata.loading && !metadata.data) return <Loading label={words.create.loadingMetadata} />;
  if (metadata.error) return <ErrorBanner error={metadata.error} onRetry={metadata.reload} />;

  if ((metadata.data?.products.length ?? 0) === 0) {
    return (
      <div className="card">
        <h1>{words.create.setUpMetadata}</h1>
        <p>{words.create.needsProduct}</p>
        <Link className="button button-primary" to="/admin/metadata">
          {words.create.goToMetadata}
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <nav className="breadcrumb">
            <Link to={ADMIN_RELEASE_NOTES_PATH}>{words.common.releaseNotes}</Link>
            <span aria-hidden="true">/</span>
            <span>{words.create.breadcrumbNew}</span>
          </nav>
          <h1>{words.common.newReleaseNote}</h1>
          <p className="muted">{words.create.intro}</p>
        </div>
      </div>

      {action.error && !duplicateFromServer && (
        <ErrorBanner error={action.error} onDismiss={action.clearError} />
      )}

      <div className="split-two">
        <div className="card">
          <div className="card-header">
            <h3>{words.create.content}</h3>
          </div>

          <Field label={words.common.title} htmlFor="new-title" required error={titleError}>
            <input
              id="new-title"
              type="text"
              value={title}
              maxLength={RELEASE_NOTE_TITLE_MAX_LENGTH}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>

          <MarkdownEditor
            value={content}
            onChange={setContent}
            noun="release note"
            allowVideo={false}
            allowAttachments={false}
            disabled={action.running}
          />
        </div>

        <div>
          <div className="card">
            <div className="card-header">
              <h3>{words.common.properties}</h3>
            </div>

            <ProductSelect
              name="release-note-product"
              error={productError}
              options={(metadata.data?.products ?? []).map((product) => ({
                id: product.productId,
                name: product.name,
                isActive: product.isActive,
              }))}
              selected={productId}
              onChange={(next) => {
                setProductId(next);
                if (duplicateFromServer) action.clearError();
              }}
            />

            <ReleaseNoteVersionInput
              id="new-version"
              value={version}
              error={versionError}
              onChange={(next) => {
                setVersion(next);
                if (duplicateFromServer) action.clearError();
              }}
            />
          </div>

          <div className="card">
            <div className="card-header">
              <h3>{words.create.publishing}</h3>
            </div>

            <div className="field">
              <label htmlFor="publish-mode">{words.create.whenVisible}</label>
              <select
                id="publish-mode"
                value={publishMode}
                onChange={(event) =>
                  setPublishMode(event.target.value as 'hidden' | 'now' | 'schedule')
                }
              >
                <option value="hidden">{words.create.publishModes.hidden}</option>
                <option value="now">{words.create.publishModes.now}</option>
                <option value="schedule">{words.create.publishModes.schedule}</option>
              </select>
              <p className="field-hint">{words.create.publishModeHints[publishMode]}</p>
            </div>

            {publishMode === 'schedule' && (
              <Field
                label={words.create.publishAt}
                htmlFor="publish-at"
                hint={storedAt ? words.create.storedAs(storedAt) : undefined}
              >
                <input
                  id="publish-at"
                  type="datetime-local"
                  value={publishAt}
                  onChange={(event) => setPublishAt(event.target.value)}
                />
              </Field>
            )}
          </div>

          <div className="button-row">
            <button
              type="button"
              className="button button-primary"
              onClick={() => void submit()}
              disabled={blocked || action.running}
            >
              {action.running ? words.create.creating : words.create.createReleaseNote}
            </button>
            <Link className="button button-quiet" to={ADMIN_RELEASE_NOTES_PATH}>
              {words.create.cancel}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
